import { Component, forwardRef, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { TextInputComponent } from '../text-input/text-input.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectOption } from '../../model/select.option';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { EventsService } from 'src/app/service/events.service';
import { NewGroupDialogComponent } from '../new-group-dialog/new-group-dialog.component';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { NavbarService } from 'src/app/service/navbar.service';
import { Submission } from 'src/app/model/paper';
import { TPCGroup } from 'src/app/model/tpc.group';
import { UserEvent } from 'src/app/model/user.event';

@Component({
  selector: 'app-select-input-group',
  templateUrl: './select-input-group.component.html',
  styleUrls: ['./select-input-group.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectInputGroupComponent),
    multi: true
  }]
})
export class SelectInputGroupComponent extends TextInputComponent implements OnInit, OnChanges {
  constructor(
    private dialog:MatDialog,
    private eventsService: EventsService,
    private navbarService: NavbarService,
    private submissionsService: SubmissionsService,
  ) {
    super();
  }

  @Input() type: string;
  @Input() options;
  @Input() indices = {
    internal: 'id',
    public: 'value'
  };
  @Input() submission: Submission;
  @Input() member: UserEvent;
  @Input() tpcGroups: Array<TPCGroup> = [];
  @Input() event: number;
  @Output() optionListChanges: EventEmitter<Array<SelectOption>> = new EventEmitter<Array<SelectOption>>();
  @Output() closed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() Update: EventEmitter<Submission> = new EventEmitter<Submission>();

  @Input() prependPublic = '';

  @Input() compact = false;
  @Input() includeNoneOption = false;

  @Input() filterable = false;
  @Input() indicesFilter: string = 'public';
  filteredOptions: Array<SelectOption>;

  @Input() LastOptionBold = false;
  @Input() readonly disabledDeleteOption = false;
  @Input() readonly disabledNewOption = false;

  /** Nome (ligature) do ícone Material Symbols exibido no chip/botão. */
  @Input() icon: string;

  private initialValue: string | null;
  public value: string;

  /** True enquanto persiste a escolha no servidor → o chip vira spinner + "Salvando…". */
  public saving = false;

  /** Id do grupo atualmente selecionado (fonte da verdade = reactive form control). */
  get currentId(): any {
    return this.formGroupController?.get(this.name)?.value;
  }

  ngOnChanges() {
    this.ngOnInit();
  }

  ngOnInit() {
    // D4: filtro/busca descartado; `filteredOptions` são os grupos reais (sem sentinela -1 —
    // o "criar grupo" virou botão dedicado no app-group-menu via requestNew).
    this.filteredOptions = this.options;
    this.setInitialValue();
  }

  private setInitialValue(): void {
    this.initialValue = this.formGroupController.get(this.name)?.value;
    this.value = this.getDisplayValue(this.initialValue);
  }

  /** Chamado pelo app-group-menu ao escolher um grupo (ou "nenhum" → null). */
  public onSelect(value: any): void {
    this.formGroupController.get(this.name)?.setValue(value);
    switch (this.type) {
      case 'paperGroup':
        this.submitPaperGroupChange(value);
        this.submission.paperGroup = value;
        this.Update.emit(this.submission);
        break;
      case 'TPCGroupSubmission':
        this.submitTPCGroupSubmission(value);
        break;
      case 'TPCGroupMember':
        this.submitTPCGroupMember(value);
        break;
    }
  }

  /** Chamado pelo app-group-menu ao pedir "criar grupo". */
  public onRequestNew(): void {
    switch (this.type) {
      case 'paperGroup':
        this.openNewPaperGroupDialog();
        break;
      case 'TPCGroupSubmission':
      case 'TPCGroupMember':
        this.openNewTPCGroupSubmissionDialog();
        break;
    }
  }

  private submitPaperGroupChange(paperGroupID: number): void {
    if (paperGroupID === -1) {
      this.openNewPaperGroupDialog();
      return;
    }

    this.saving = true;   // chip vira spinner + "Salvando…" até o finalize
    this.navbarService.loadingStart();

    const submissionInfo = {
      title: this.submission.title,
      status: this.submission.status,
      paperGroup: paperGroupID,
    }

    this.submissionsService.editMinimumSubmission(submissionInfo, this.submission.id)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.value = this.getDisplayValue(submissionInfo.paperGroup);
      });

    this.navbarService.loadingStop();
  }

  private submitTPCGroupSubmission(tpcGroupSubmissionID: number): void {
    if (tpcGroupSubmissionID === -1) {
      this.openNewTPCGroupSubmissionDialog();
      return;
    }

    this.saving = true;
    this.navbarService.loadingStart();

    const submissionInfo = {
      title: this.submission.title,
      status: this.submission.status,
      tpcGroup: tpcGroupSubmissionID,
    };

    this.submissionsService.editMinimumSubmission(submissionInfo, this.submission.id)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.value = this.getDisplayValue(submissionInfo.tpcGroup);
      });

    this.navbarService.loadingStop();
  }

  private submitTPCGroupMember(tpcGroupMemberID: number): void {
    if (tpcGroupMemberID === -1) {
      this.openNewTPCGroupSubmissionDialog();
      return;
    }

    this.saving = true;
    this.member.tpcGroup = tpcGroupMemberID;

    this.eventsService.editEventPeople(this.member, this.event)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.value = this.getDisplayValue(this.member.tpcGroup);
      });
  }

  // Notificações de sucesso (trocar / criar / excluir grupo) removidas: o spinner
  // "Salvando…" no chip já sinaliza a operação até o servidor responder.

  public openNewPaperGroupDialog(): void {
    const dialogConfig = this.buildDialogConfig('admin.event.groups.labels.add-new');

    const dialogRef = this.dialog.open(NewGroupDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(data => this.addGroup(data));
  }

  private openNewTPCGroupSubmissionDialog(): void {
    const dialogConfig = this.buildDialogConfig('admin.event.people.member-groups.new-group.title');

    const dialogRef = this.dialog.open(NewGroupDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(data => this.addTPCGroupSubmission(data));
  }

  private buildDialogConfig(title?: string, params?: {}, content?): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title,
      params,
      content
    };

    return dialogConfig;
  }

  private addGroup(data: {group: string}): void {
    if (!!data) {
      // Spinner cobre criar grupo + atribuir; o submit* (chamado abaixo) tem o finalize
      // que zera `saving` ao fim de TODA a cadeia. O 2º arg (erro) limpa se o criar falhar.
      this.saving = true;
      this.eventsService.addEventPaperGroup(this.event, data.group).subscribe(group => {
        this.addOption(group.name, group.id)
        this.formGroupController.controls[this.name].setValue(group.id);
        this.value = this.getDisplayValue(group.id);
        this.reorderGroups();
        this.submitPaperGroupChange(group.id);
        this.optionListChanges.emit(this.filteredOptions);
      }, () => this.saving = false);
      return;
    }
    this.resetForm();
  }

  private addTPCGroupSubmission(data: { group: string }): void {
    if (!!data) {
      this.saving = true;
      this.eventsService.addEventTPCGroup(this.event, data.group).subscribe(group => {
        this.addOption(group.name, group.id)
        this.formGroupController.controls[this.name].setValue(group.id);
        this.reorderGroups();
        (this.type === 'TPCGroupSubmission') ? this.submitTPCGroupSubmission(group.id) : this.submitTPCGroupMember(group.id);
        this.optionListChanges.emit(this.filteredOptions);
      }, () => this.saving = false);
      return;
    }
    this.resetForm();
  }

  private resetForm(): void {
    this.formGroupController.get(this.name).setValue(this.initialValue);
    this.setInitialValue();
  }

  private addOption(label: string, id: number): void {
    const option: SelectOption = new SelectOption(id, label);
    this.filteredOptions.push(option);
  }

  private reorderGroups(): void {
    this.sortOptions();
  }

  private sortOptions(): void {
    this.filteredOptions = this.filteredOptions.sort((a, b) => a.value.localeCompare(b.value));
  }

  public removeGroupDialog(group: string): void {
    let dialogConfig: MatDialogConfig;
    let title: string;
    const params = { name: `"${group}"` };
    switch (this.type) {
      case 'paperGroup':
        title = 'admin.event.paper-groups.confirm-delete-group';
        dialogConfig = this.buildDialogConfig(title, params);
        break;
      case 'TPCGroupSubmission':
        title = 'admin.event.people.member-groups.confirm-delete-tpc-group';
        dialogConfig = this.buildDialogConfig(title, params);
        break;
      case 'TPCGroupMember':
        title = 'admin.event.people.member-groups.confirm-delete-tpc-group';
        dialogConfig = this.buildDialogConfig(title, params);
        break;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        switch (this.type) {
          case 'paperGroup':
            this.deletePaperGroup(group);
            break;
          case 'TPCGroupSubmission':
            this.deleteTPCGroup(group);
            break;
          case 'TPCGroupMember':
            this.deleteTPCGroup(group);
            break;
        }
      }
    });
  }

  private deletePaperGroup($group: string): void {
    const groupID = this.filteredOptions.find(group => group.value === $group).id;
    this.saving = true;
    this.eventsService.deleteEventPaperGroup(this.event, groupID)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.filteredOptions = this.filteredOptions.filter(group => group.id !== groupID);
        this.optionListChanges.emit(this.filteredOptions);
      });
  }

  private deleteTPCGroup(TPCGroup: string): void {
    const groupID = this.filteredOptions.find(group => group.value === TPCGroup).id;
    this.saving = true;
    this.eventsService.deleteEventTPCGroup(this.event, groupID)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        this.filteredOptions = this.filteredOptions.filter(group => group.id !== groupID);
        this.optionListChanges.emit(this.filteredOptions);
      });
  }

  private getDisplayValue(value: any): string {
    let displayValue: any;
    this.filteredOptions.forEach((option: SelectOption) => {
      if ((option[this.indices.internal]) === value) {
        displayValue = `${this.prependPublic}${option[this.indices.public]}`;
      }
    });

    return displayValue;
  }

  public onClosed(): void {
    this.closed.emit(true);
  }
}
